/// <amd-module name="Tfs/ReduxCommon/Modules/Teams/TeamSource" />
import { ITfsPageService } from "Tfs/Platform/Page";
import { ICoreRestClient } from "Tfs/Platform/RestClient/Core";
import { ITeam } from "Tfs/ReduxCommon/Contracts/Team";
import { IVssPageContext } from "VSS/Platform/Context";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";

export interface ITeamSource {
    fetchTeamsForCurrentProject(pageSize?: number): Promise<ITeam[]>;
    /**
     * Fetch and page all teams for a given project
     * @param projectId The project ID
     * @param pageSize The page size. @default 1000
     */
    fetchTeamsForProject(projectId: string, pageSize?: number): Promise<ITeam[]>;
}

// TODO: How is this setting materialized?
export const TEAM_PAGE_SIZE_KEY = "Agile/TeamSource/TeamPageSize";
export const TEAM_PAGE_SIZE = 1000;

/**
 * The team source contains REST API calls relating to Teams
 */
export class TeamSource implements ITeamSource {
    private _pageContext: IVssPageContext;

    constructor(pageContext: IVssPageContext) {
        this._pageContext = pageContext;
    }

    public fetchTeamsForCurrentProject(pageSize?: number): Promise<ITeam[]> {
        const pageService = this._pageContext.getService<ITfsPageService>("ITfsPageService");
        const pageData = pageService.getData();
        if (pageData && pageData.project) {
            const projectId = pageData.project.id;

            return this.fetchTeamsForProject(projectId, pageSize);
        }

        return Promise.reject(new Error("Current Project could not be determined"));
    }

    public fetchTeamsForProject(projectId: string, pageSize?: number): Promise<ITeam[]> {
        if (pageSize == null) {
            const settingsService = this._getSettingsService();
            pageSize = settingsService.getEntry<number>(TEAM_PAGE_SIZE_KEY, SettingsUserScope.Host) || TEAM_PAGE_SIZE;
        }

        const restClient = this._getRestClient();
        const getTeams = (top: number, skip: number) => restClient.getTeams(projectId, false /* all teams */, top, skip);

        return batchGet(getTeams, pageSize);
    }

    private _getRestClient(): ICoreRestClient {
        return this._pageContext.getRestClient<ICoreRestClient>("ICoreRestClient");
    }

    private _getSettingsService(): ISettingsService {
        return this._pageContext.getService<ISettingsService>("ISettingsService");
    }
}

function batchGet<T>(get: (top: number, skip: number) => Promise<T[]>, top = 500, skip = 0): Promise<T[]> {
    return get(top, skip).then((items: T[]) => {
        if (items.length < top) {
            // If we already have all the items, return them
            return items;
        } else {
            // There are still more items on the server, so make another call (skipping what we already have), append the results and return the combined array
            return batchGet(get, top, skip + items.length).then(extraItems => items.concat(extraItems));
        }
    });
}
